if (!customElements.get("product-bundle-info")) {
  class ProductBundleInfo extends HTMLElement {
    constructor() {
      super();

      /*
       * 注意：
       * bundleItems 在 DOM 交换位置之前获取。
       *
       * 因此即使 AMS Priority 开启后：
       *
       * 页面展示顺序：
       * 原第二个 Bundle
       * 原第一个主商品
       * 原第三个 Bundle
       * ...
       *
       * 但 bundleItems 内部顺序仍然是：
       * index 0 = 原第一个主商品
       * index 1 = 原第二个 Bundle
       * index 2 = 原第三个 Bundle
       *
       * 所以不会破坏原来的 index == 0 业务逻辑。
       */
      this.bundleItems = this.querySelectorAll(".bundle-item");
      this.firstItems = this.querySelectorAll(".bundle-first");

      this.handleItemClick = this.handleItemClick.bind(this);
      this.handleItemKeydown = this.handleItemKeydown.bind(this);
    }

    connectedCallback() {
      /*
       * 新增：
       * 根据 custom.ams_priority_bundle
       * 判断是否需要将第二个 Bundle 提到第一个。
       */
      this.initAmsPriorityBundle();

      /*
       * 原逻辑保持不变
       */
      this.initEventListeners();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    /*
     * ============================================================
     * AMS Priority Bundle
     * ============================================================
     *
     * metafield:
     * custom.ams_priority_bundle
     *
     * false:
     * 完全不执行任何修改，保持原逻辑。
     *
     * true:
     * 原第二个 Bundle 与原第一个商品交换展示位置，
     * 并默认选中原第二个 Bundle。
     *
     * 注意：
     * 这里只交换 DOM 位置，
     * 不重新 querySelectorAll，
     * 保证 bundleItems 的 index 顺序不发生改变。
     * ============================================================
     */
    initAmsPriorityBundle() {
      const isPriorityBundle =
        this.getAttribute("data-ams-priority-bundle") === "true";

      /*
       * 没开启：
       * 什么都不做
       */
      if (!isPriorityBundle) return;

      /*
       * 至少需要两个有效 Bundle Item 才能交换
       */
      if (!this.bundleItems || this.bundleItems.length < 2) return;

      /*
       * 原始顺序：
       *
       * bundleItems[0] = 当前 PDP 主商品
       * bundleItems[1] = 第二个 Bundle
       */
      const originalFirstItem = this.bundleItems[0];
      const originalSecondItem = this.bundleItems[1];

      if (!originalFirstItem || !originalSecondItem) return;

      const parent = originalFirstItem.parentNode;

      if (!parent) return;

      /*
       * ============================================================
       * 只交换 DOM 展示位置
       * ============================================================
       *
       * before:
       *
       * A 主商品
       * B Bundle
       * C Bundle
       *
       * after:
       *
       * B Bundle
       * A 主商品
       * C Bundle
       *
       * bundleItems NodeList 自身仍保持：
       *
       * [A, B, C]
       *
       * 所以原来的 index == 0 判断仍然正确。
       */
      parent.insertBefore(originalSecondItem, originalFirstItem);

      /*
       * 默认选中第二个 Bundle
       *
       * Liquid 本身已经处理 active，
       * 这里再做一次兜底，
       * 防止其他脚本提前修改 active。
       */
      this.bundleItems.forEach((item) => {
        item.classList.remove("active");
      });

      originalSecondItem.classList.add("active");

      /*
       * 原来的业务规则：
       *
       * index == 0
       * 主商品显示 multi attributes
       *
       * index != 0
       * Bundle 隐藏 multi attributes
       *
       * 当前默认选中的就是原来的 index 1，
       * 所以初始化时执行 hide。
       */
      const productTemplateContainer =
        this.closest(".product-template-container");

      if (productTemplateContainer) {
        productTemplateContainer.classList.add(
          "hide-multi-attributes"
        );
      }

      /*
       * 当前默认商品已经从主商品变成第二个 Bundle，
       * 所以需要主动通知其他模块：
       *
       * price
       * compare price
       * variant id
       * color
       *
       * 全部继续走原来的 bundle:InfoChange 事件。
       *
       * 使用 requestAnimationFrame，
       * 让页面其他组件先完成初始化。
       */
      requestAnimationFrame(() => {
        this.dispatchBundleInfoChange(
          this.getItemInfo(originalSecondItem)
        );
      });
    }

    initEventListeners() {
      this.firstItems.forEach((item) => {
        item.addEventListener("click", this.handleItemClick);

        // keyboard
        item.addEventListener(
          "keydown",
          this.handleItemKeydown
        );
      });
    }

    handleItemKeydown(e) {
      if (
        e.key === "Enter" ||
        e.key === "NumpadEnter" ||
        e.key === " "
      ) {
        e.preventDefault();

        this.handleItemClick(e);
      }
    }

    removeEventListeners() {
      this.firstItems.forEach((item) => {
        item.removeEventListener(
          "click",
          this.handleItemClick
        );

        item.removeEventListener(
          "keydown",
          this.handleItemKeydown
        );
      });
    }

    getItemInfo(item) {
      const itemPriceEl =
        item.querySelector(".bundle-on-sale");

      const itemPrice = itemPriceEl
        ? Number(
            itemPriceEl.getAttribute("data-content")
          )
        : 0;

      const comparePriceEl =
        item.querySelector(".bundle-compare-at");

      const comparePrice = comparePriceEl
        ? Number(
            comparePriceEl.getAttribute("data-content")
          )
        : itemPrice;

      const id = item.getAttribute("data-id");

      const rawColor =
        item.getAttribute("data-color") ||
        item
          .querySelector(".bundle-color")
          ?.textContent?.trim() ||
        "";

      const color =
        rawColor === "Default Title"
          ? ""
          : rawColor || "";

      return {
        itemPrice,
        comparePrice,
        id,
        color,
      };
    }

    handleItemClick(e) {
      const clickedItem = e.currentTarget;

      const bundleItem =
        clickedItem.closest(".bundle-item");

      if (!bundleItem) return;

      /*
       * 这里保持你的原逻辑。
       *
       * 即使页面上 AMS Bundle 已经移动到第一位，
       * bundleItems 内部 index 顺序没有变化。
       */
      this.bundleItems.forEach((item, index) => {
        if (item === bundleItem) {
          if (item.classList.contains("active")) return;

          item.classList.add("active");

          if (index == 0) {
            clickedItem
              .closest(".product-template-container")
              .classList.remove(
                "hide-multi-attributes"
              );
          } else {
            clickedItem
              .closest(".product-template-container")
              .classList.add(
                "hide-multi-attributes"
              );
          }
        } else {
          item.classList.remove("active");
        }
      });

      this.dispatchBundleInfoChange(
        this.getItemInfo(bundleItem)
      );
    }

    dispatchBundleInfoChange({
      itemPrice,
      comparePrice,
      id,
      color,
    }) {
      this.dispatchEvent(
        new CustomEvent("bundle:InfoChange", {
          bubbles: true,
          cancelable: true,

          detail: {
            itemPrice: itemPrice,
            comparePrice: comparePrice,
            id: id,
            color: color,
          },
        })
      );
    }
  }

  customElements.define(
    "product-bundle-info",
    ProductBundleInfo
  );
}